import React, { useContext, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import HrnbCard from "../controls/HrnbCard";
import HrnbButton from "../controls/HrnbButton";
import HrnbModal from "../controls/HrnbModal";
import "../../css/components/BattleCard.css";
import {getFormatIcon, getFormatLabel} from "../../utils/battleFormats";
import { DetailsIcon, JoinIcon, RegisterIcon } from "../../icons";

const STATUS_COLORS = {
    PLANNED: "#17a2b8",
    ACTIVE: "#28a745",
    FINISHED: "#6c757d",
    CANCELLED: "#dc3545",
};

const TYPE_LABELS = {
    SINGLE: "Индивидуальный",
    TEAM: "Командный",
};

const STATUS_LABELS = {
    PLANNED: "Запланирован",
    ACTIVE: "Активен",
    FINISHED: "Завершён",
    CANCELLED: "Отменён",
};

const FORMAT_FULL_LABELS = {
    AUDIO: "🎵 Аудио",
    TEXT: "📝 Текст",
};

const VISIBILITY_LABELS = {
    PUBLIC: "Публичный",
    PRIVATE: "Частный",
    INVITE_ONLY: "По приглашению",
};

export default function BattleCard({ battle, coverUrl }) {
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const [showAuthModal, setShowAuthModal] = useState(false);

    const {
        id,
        title,
        description,
        type,
        status,
        format,
        visibility,
        startTime,
        endTime,
        prizePool,
        maxParticipants,
        participantCount = 0,
        organizerCount = 0,
        judgeCount = 0,
        highlightedTitle,
        tags = [],
    } = battle;

    // Используем переданный coverUrl (из хука useBattleCovers)
    const displayCoverUrl = coverUrl || null;

    const statusColor = STATUS_COLORS[status] || "#6c757d";
    const typeLabel = TYPE_LABELS[type] || type || "Не указано";
    const statusLabel = STATUS_LABELS[status] || status || "Не указано";
    const formatLabel = FORMAT_FULL_LABELS[format] || format;
    const formatIcon = getFormatIcon(format);
    const visibilityLabel = VISIBILITY_LABELS[visibility] || visibility;
    const formatDate = (dateStr) => {
        if (!dateStr) return "Не указано";
        const date = new Date(dateStr);
        return date.toLocaleDateString("ru-RU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const handleViewDetails = () => {
        navigate(`/battles/${id}`);
    };

    const handleJoin = (e) => {
        e.stopPropagation();
        // Если пользователь не авторизован, показываем модальное окно
        if (!currentUser) {
            setShowAuthModal(true);
        } else {
            navigate(`/battles/${id}`);
        }
    };

    const handleCloseModal = () => {
        setShowAuthModal(false);
    };

    return (
        <>
        <HrnbCard className="battle-card">
            {displayCoverUrl && displayCoverUrl !== 'loading' && (
                <div className="battle-card-cover">
                    <img
                        src={displayCoverUrl}
                        alt={title}
                        className="battle-card-cover-image"
                        onError={(e) => {
                            e.target.style.display = 'none';
                            console.warn('Failed to load cover image:', displayCoverUrl);
                        }}
                    />
                </div>
            )}

            <div className="battle-card-header">
                <span className="battle-card-status" style={{ backgroundColor: statusColor }}>
                    {statusLabel}
                </span>
                <span className="battle-card-type">{typeLabel}</span>
                {format && (
                    <span
                        className={`battle-card-format battle-card-format--${format.toLowerCase()}`}
                        title={getFormatLabel(format)}
                    >
                        <span className="battle-card-format-icon">{formatIcon}</span>
                        <span className="battle-card-format-text">{getFormatLabel(format)}</span>
                    </span>
                )}
            </div>

            <div className="battle-card-body">
                <h3 className="battle-card-title">
                    {highlightedTitle || title}
                </h3>

                {description && (
                    <p className="battle-card-description">
                        {description.length > 100
                            ? `${description.substring(0, 100)}...`
                            : description}
                    </p>
                )}

                {tags.length > 0 && (
                    <div className="battle-card-tags">
                        {tags.slice(0, 3).map((tag, idx) => (
                            <span key={idx} className="battle-card-tag">{tag}</span>
                        ))}
                        {tags.length > 3 && (
                            <span className="battle-card-tag battle-card-tag--more">+{tags.length - 3}</span>
                        )}
                    </div>
                )}

                <div className="battle-card-info">
                    {startTime && (
                        <div className="battle-card-info-item">
                            <span className="battle-card-info-label">Начало:</span>
                            <span className="battle-card-info-value">{formatDate(startTime)}</span>
                        </div>
                    )}

                    {endTime && (
                        <div className="battle-card-info-item">
                            <span className="battle-card-info-label">Конец:</span>
                            <span className="battle-card-info-value">{formatDate(endTime)}</span>
                        </div>
                    )}

                    {prizePool && (
                        <div className="battle-card-info-item">
                            <span className="battle-card-info-label">Призовой фонд:</span>
                            <span className="battle-card-info-value">{prizePool}</span>
                        </div>
                    )}

                    {maxParticipants && (
                        <div className="battle-card-info-item">
                            <span className="battle-card-info-label">Участники:</span>
                            <span className="battle-card-info-value">
                                {participantCount || 0} / {maxParticipants}
                            </span>
                        </div>
                    )}

                    {!maxParticipants && participantCount !== undefined && (
                        <div className="battle-card-info-item">
                            <span className="battle-card-info-label">Участников:</span>
                            <span className="battle-card-info-value">{participantCount}</span>
                        </div>
                    )}

                    {organizerCount > 0 && (
                        <div className="battle-card-info-item">
                            <span className="battle-card-info-label">Организаторов:</span>
                            <span className="battle-card-info-value">{organizerCount}</span>
                        </div>
                    )}

                    {judgeCount > 0 && (
                        <div className="battle-card-info-item">
                            <span className="battle-card-info-label">Жюри:</span>
                            <span className="battle-card-info-value">{judgeCount}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="battle-card-footer">
                <HrnbButton variant="primary" onClick={handleViewDetails} icon={<DetailsIcon/>}>
                    Подробнее
                </HrnbButton>
                {(status === "PLANNED" || status === "ACTIVE") && visibility === "PUBLIC" && (
                    <HrnbButton variant="success" onClick={handleJoin} icon={<JoinIcon/>}>
                        Участвовать
                    </HrnbButton>
                )}
            </div>
        </HrnbCard>

        {/* Модальное окно для неавторизованных пользователей */}
        <HrnbModal
            open={showAuthModal}
            onClose={handleCloseModal}
            title="Требуется авторизация"
            size="medium"
        >
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <p style={{ fontSize: '16px', marginBottom: '24px' }}>
                    Для участия в баттле необходимо войти в систему или зарегистрироваться
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <Link to="/login">
                        <HrnbButton variant="primary" onClick={handleCloseModal}>
                            Войти
                        </HrnbButton>
                    </Link>
                    <Link to="/register">
                        <HrnbButton variant="outline" onClick={handleCloseModal} icon={<RegisterIcon />}>
                            Регистрация
                        </HrnbButton>
                    </Link>
                </div>
            </div>
        </HrnbModal>
        </>
    );
}
